import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { checkType, CtyShowToast, getUserInitials, getUserName } from '@circuitry-ai/doc-ui';
import {
  getApiBaseUrl,
  resolveCallLLMActionSourceId,
  streamWebAgentBuild,
  useAppDispatch,
  type RootState,
  type WebAgentBuildResult,
} from '@circuitry-ai/doc-data';
import { SharedConverseUI, toConversePhase, useWebAgentConverse } from '@circuitry-ai/doc-chatadvisor';

/** What the assistant generated: browser steps or post-processing custom instructions. */
export type AssistantTarget = 'steps' | 'custom';

/** How generated entries merge into the existing editor list. */
export type AssistantMode = 'append' | 'replace' | 'range' | 'single';

export interface AssistantApplyPayload {
  target: AssistantTarget;
  mode: AssistantMode;
  entries: any[];
  range?: { start: number; end: number };
}

export interface WebAgentInstructionsAssistantProps {
  readonly agent?: any;
  readonly isReadOnly?: boolean;
  /** Configured start URL for the agent; the build runner opens it to browse the live site. */
  readonly startUrl?: string;
  /** Current steps in the editor — sent as context so the runner can decide target/apply position. */
  readonly existingSteps?: any[];
  /** Current custom instructions in the editor — sent as context alongside the steps. */
  readonly existingCustom?: any[];
  /** Command/user input names; the runner auto-templates matching values as ${name}. */
  readonly availableInputs?: string[];
  /**
   * Hides the panel without unmounting it. Collapsing must not discard the conversation, so the parent
   * keeps this mounted for as long as the Instructions tab is open and only hides it here.
   */
  readonly isHidden?: boolean;
  readonly onApply: (payload: AssistantApplyPayload) => void;
}

const LLM_MODEL = 'claude-opus-4-8';

/** Sum phase durations for the workflow accordion / total-time tooltip. */
function sumPhaseSeconds(msg: { phases?: Array<{ duration_sec?: number }> }): number {
  return (msg.phases ?? []).reduce((sum, p) => sum + (Number(p.duration_sec) || 0), 0);
}

/** Build the "applied …" confirmation line shown in the converse thread. */
function appliedNote(t: (k: string, o?: any) => string, target: AssistantTarget, mode: AssistantMode, count: number, from?: number, to?: number): string {
  const kind = target === 'custom' ? t('custom instruction(s)') : t('step(s)');
  switch (mode) {
    case 'replace':
      return t('Replaced all {{kind}} with {{count}} new one(s).', { kind, count });
    case 'range':
      return t('Replaced {{kind}} {{from}}–{{to}} with {{count}} new one(s).', { kind, from, to, count });
    case 'single':
      return t('Replaced {{kind}} {{from}} with {{count}} new one(s).', { kind, from, count });
    default:
      return t('Appended {{count}} {{kind}}.', { count, kind });
  }
}

export function WebAgentInstructionsAssistant({ agent, isReadOnly, startUrl, existingSteps, existingCustom, availableInputs, isHidden, onApply }: WebAgentInstructionsAssistantProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { details: user, isAuthenticated } = useSelector((state: RootState) => state.auth.user);
  const token = useSelector((state: RootState) => state.auth.token) as string | null;
  const { converseMessages, isStreaming, sendingRef, patchActive, appendPhase, beginTurn, endTurn } = useWebAgentConverse();

  /** Set when the agent paused with a question, so the next message is sent as an answer with its context. */
  const pendingRef = useRef<{ prompt: string; question: string } | null>(null);

  /** Turn the runner's result into an apply payload (validating range/single against the editor list). */
  const finalizeBuild = (result: WebAgentBuildResult, prompt: string) => {
    const target: AssistantTarget = result.target === 'custom' ? 'custom' : 'steps';
    const entries = Array.isArray(result.entries) ? result.entries : [];
    const summary = typeof result.summary === 'string' ? result.summary : '';
    const question = typeof result.question === 'string' ? result.question.trim() : '';
    const { from, to } = result;
    pendingRef.current = question ? { prompt, question } : null;

    if (entries.length === 0) {
      patchActive((msg) => ({
        ...msg,
        textMessage: question || summary || t('No changes were generated for that request.'),
        total_time_taken_seconds: sumPhaseSeconds(msg),
      }));
      return;
    }

    const count = (target === 'custom' ? existingCustom : existingSteps)?.length ?? 0;
    const valid = (n?: number) => Number.isFinite(n) && (n as number) >= 1 && (n as number) <= count;
    let mode = (result.mode as AssistantMode) ?? 'append';
    let range: AssistantApplyPayload['range'];
    if (mode === 'range' && valid(from) && valid(to) && (to as number) >= (from as number)) {
      range = { start: (from as number) - 1, end: to as number };
    } else if (mode === 'single' && valid(from)) {
      range = { start: (from as number) - 1, end: (from as number) - 1 };
    } else if (mode === 'range' || mode === 'single') {
      mode = 'append';
    }

    const prefix = summary ? `${summary}\n\n` : '';
    const tail = question ? `\n\n${question}` : ` ${t('Review and click Save.')}`;
    patchActive((msg) => ({
      ...msg,
      textMessage: `${prefix}${appliedNote(t, target, mode, entries.length, from, to)}${tail}`,
      total_time_taken_seconds: sumPhaseSeconds(msg),
    }));
    onApply({ target, mode, entries, range });
  };

  const handleSend = async (userInput: string) => {
    const message = userInput?.trim();
    if (!message || isReadOnly || sendingRef.current) return;

    // Answering a question the agent asked: resend the original request with the Q&A appended so the
    // runner has the full context instead of just the bare answer.
    const pending = pendingRef.current;
    pendingRef.current = null;
    const prompt = pending ? `${pending.prompt}\n\nYou asked: ${pending.question}\nMy answer: ${message}` : message;

    const signal = beginTurn(
      { name: getUserName(isAuthenticated, user), textMessage: message, viewLogo: false, initials: getUserInitials(user?.nickname) },
      { name: agent?.name ?? t('Assistant'), textMessage: '', phases: [], viewLogo: true },
    );

    try {
      const sourceId = await resolveCallLLMActionSourceId(dispatch);
      await streamWebAgentBuild({
        prompt,
        startUrl,
        inputs: (availableInputs ?? []).map((name) => ({ name })),
        existingSteps,
        existingCustom,
        token,
        llm: { apiBase: getApiBaseUrl(), sourceId, model: LLM_MODEL },
        signal,
        onEvent: (event) => {
          if (event.type === 'step') appendPhase(toConversePhase(event, t('Step')));
          else if (event.type === 'done') finalizeBuild(event.result, prompt);
          else if (event.type === 'error') {
            patchActive((msg) => ({
              ...msg,
              textMessage: event.message,
              total_time_taken_seconds: sumPhaseSeconds(msg),
            }));
          }
        },
      });
    } catch (error: any) {
      CtyShowToast(checkType.error, error?.data?.message ?? error?.message ?? t('Failed to generate instructions.'));
    } finally {
      endTurn();
    }
  };

  return (
    <div className={`${isHidden ? 'hidden' : 'flex'} h-full min-h-0 w-[35%] shrink-0 flex-col border-l border-divider bg-content1`}>
      <div className="flex-1 min-h-0 p-2">
        <SharedConverseUI
          name={t('Instructions Assistant')}
          converseMessages={converseMessages}
          responseLoading={isStreaming}
          handleSend={handleSend}
          enableFile={false}
          enableSpeak={false}
          showStepDuration
          placeholder={t('Describe what you want in plain English. The assistant decides whether to add steps or custom instructions and how to apply them.')}
        />
      </div>
    </div>
  );
}

export default WebAgentInstructionsAssistant;
